import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type {
  MemberPreferences, MyPreferencesResponse, PartnerAppEntry, PartnerSettingsData,
  PartnerStatement,
} from '../../types/aai';
import { ErrorNote, Loading } from '../../components/aa';

/*
 * The five sections that belong to the SOLUTION PARTNER rather than to the
 * client's own people.
 *
 * That distinction is the whole reason these are not the employee panels: a
 * partner's boundary is their assignment, their app catalog is what that
 * assignment reaches, and their account sits in their own firm. Reusing the
 * client-facing versions here would have described the wrong person.
 *
 * Nothing is invented. Where a policy is not stored the row says so, and the
 * control that would set it is disabled with the reason attached.
 */

const INPUT = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-ink';
const READONLY = 'w-full rounded-card border border-line bg-canvas-inset px-4 py-3 text-[14px] text-ink-muted';

export const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label, hint, children,
}) => (
  <label className="block">
    <span className="mb-2 block text-[13px] font-semibold text-ink">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[12px] text-ink-soft">{hint}</span>}
  </label>
);

const SaveBar: React.FC<{
  dirty: boolean; busy: boolean; label: string; onSave: () => void; onRevert: () => void;
}> = ({ dirty, busy, label, onSave, onRevert }) => (
  <div className="mt-6 flex flex-wrap items-center justify-end gap-2.5 border-t border-line pt-5">
    <button
      type="button"
      onClick={onRevert}
      disabled={!dirty || busy}
      className="h-11 rounded-act border border-line-strong bg-white px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas-inset disabled:opacity-40"
    >
      Revert
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={!dirty || busy}
      className="h-11 rounded-act bg-ink px-5 text-[13px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-40"
    >
      {busy && 'Saving…'}
      {!busy && label}
    </button>
  </div>
);

/* ── General: the partner's own preferences ─────────────────────────────── */

// The four the platform actually stores on a member. Start pages are the
// partner console's own destinations, so a saved page always resolves.
const START_PAGES = [
  { value: 'home', label: 'Clients' },
  { value: 'projects', label: 'Projects' },
  { value: 'adoption', label: 'AI Adoption' },
  { value: 'results', label: 'Results' },
];

const DENSITY = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

const LANGUAGES = [{ value: 'en', label: 'English' }];

const TIMEZONES = [
  'UTC', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles',
  'Asia/Karachi', 'Asia/Dubai', 'Asia/Singapore', 'Australia/Sydney',
];

export const GeneralSection: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { data, loading, error, reload } = useAsync<MyPreferencesResponse>(
    () => aaiApi.getMyPreferences(), 'Could not load your preferences.', [clientId],
  );
  const [draft, setDraft] = useState<Partial<MemberPreferences>>({});
  const [busy, setBusy] = useState(false);

  const saved = data?.preferences;
  useEffect(() => { setDraft({}); }, [saved]);

  if (loading) return <Loading label="Loading preferences" />;
  if (error || !saved) return <ErrorNote message={error || 'Something went wrong.'} />;

  const value = <K extends keyof MemberPreferences>(key: K): MemberPreferences[K] => {
    if (draft[key] !== undefined) return draft[key] as MemberPreferences[K];
    return saved[key];
  };
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      await aaiApi.saveMyPreferences(draft);
      setDraft({});
      reload();
      toast.success('Preferences saved.');
    } catch {
      toast.error('Could not save your preferences.');
    } finally {
      setBusy(false);
    }
  };

  // A time zone the person already has but the list does not offer must still
  // be selectable, or saving anything else would silently move them.
  const zones = TIMEZONES.includes(value('timezone')) ? TIMEZONES : [value('timezone'), ...TIMEZONES];

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Start page">
          <select
            value={value('start_page')}
            onChange={(e) => setDraft({ ...draft, start_page: e.target.value })}
            className={INPUT}
          >
            {START_PAGES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Interface density">
          <select
            value={value('density')}
            onChange={(e) => setDraft({ ...draft, density: e.target.value })}
            className={INPUT}
          >
            {DENSITY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Language">
          <select
            value={value('language')}
            onChange={(e) => setDraft({ ...draft, language: e.target.value })}
            className={INPUT}
          >
            {LANGUAGES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Time zone">
          <select
            value={value('timezone')}
            onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
            className={INPUT}
          >
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
      </div>
      <SaveBar
        dirty={dirty}
        busy={busy}
        label="Save preferences"
        onSave={save}
        onRevert={() => setDraft({})}
      />
    </div>
  );
};

/* ── Account ────────────────────────────────────────────────────────────── */

const initialsOf = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const when = (iso: string | null): string => {
  if (!iso) return 'unknown';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const AccountRow: React.FC<{
  kicker: string; label: string; sub: string; open: boolean; onToggle: () => void;
  children?: React.ReactNode;
}> = ({ kicker, label, sub, open, onToggle, children }) => (
  <div className="border-t border-line-soft first:border-t-0">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-canvas"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-canvas-inset font-sans text-[12px] font-bold text-ink-muted">
        A
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12px] text-ink-dim">{kicker}</span>
        <span className="block text-[13.5px] font-bold text-ink">{label}</span>
        <span className="block text-[12.5px] text-ink-dim">{sub}</span>
      </span>
      <span className="shrink-0 text-[13px] font-semibold text-ink-muted">
        {open && 'Close'}
        {!open && 'Open →'}
      </span>
    </button>
    {open && children && <div className="px-5 pb-4">{children}</div>}
  </div>
);

export const AccountSection: React.FC<{ clientId: string; data: PartnerSettingsData }> = ({
  clientId, data,
}) => {
  const account = data.account;
  const [name, setName] = useState(account.display_name || '');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(account.display_name || ''); }, [account.display_name, clientId]);

  const dirty = name.trim() !== (account.display_name || '');

  const save = async () => {
    setBusy(true);
    try {
      // The display name lives on the user record, which the profile endpoint
      // owns. Saving it here keeps the sidebar and this screen in step.
      await aaiApi.editProfileSummary(name.trim());
      localStorage.setItem('user_name', name.trim());
      toast.success('Account saved.');
    } catch {
      toast.error('Could not save your account.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) => setOpenRow((current) => {
    if (current === id) return null;
    return id;
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-panel bg-canvas-inset px-5 py-5 md:px-6">
        <div className="flex min-w-0 items-center gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-tile bg-ink text-[15px] font-bold text-white">
            {initialsOf(account.display_name || account.email)}
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Account</div>
            <div className="mt-0.5 text-[20px] font-bold leading-tight text-ink">
              {account.display_name || account.email}
            </div>
            <div className="text-[12.5px] text-ink-dim">
              {account.team || 'No firm recorded'} · {account.established_role}
            </div>
          </div>
        </div>
        <span className="shrink-0 rounded-md bg-ok-soft px-2.5 py-1 text-[12px] font-semibold text-[#3F7A2F]">
          Active
        </span>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field label="Display name">
          <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Work email" hint="Your sign-in address. An admin changes it.">
          <input value={account.email} readOnly className={READONLY} />
        </Field>
        <Field label="Team" hint="Organisation managed">
          <input value={account.team || 'Not recorded'} readOnly className={READONLY} />
        </Field>
        <Field label="Established role" hint="Owner managed">
          <input value={account.established_role} readOnly className={READONLY} />
        </Field>
      </div>

      <div className="mt-5 overflow-hidden rounded-panel border border-line bg-white">
        <AccountRow
          kicker="Organisation managed"
          label="Sign-in method"
          sub={account.sign_in_method}
          open={openRow === 'signin'}
          onToggle={() => toggle('signin')}
        >
          <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
            Your organisation controls how you sign in. Changing the method, or adding a second factor, is
            done by an admin rather than here.
          </p>
        </AccountRow>

        <AccountRow
          kicker={`${account.sessions.length} live`}
          label="Active sessions"
          sub="Signed-in browsers and devices that can reach your account now"
          open={openRow === 'sessions'}
          onToggle={() => toggle('sessions')}
        >
          <div className="divide-y divide-line-soft rounded-card border border-line">
            {account.sessions.length === 0 && (
              <p className="m-0 px-4 py-4 text-[13px] text-ink-dim">No live session is recorded.</p>
            )}
            {account.sessions.map((session) => (
              <div key={session.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-ink">{session.user_agent}</div>
                  <div className="text-[12px] text-ink-dim">
                    {session.ip || 'No address recorded'} · last seen {when(session.last_seen_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AccountRow>

        <AccountRow
          kicker="Current"
          label="Devices & recovery"
          sub="Device trust · organisation recovery"
          open={openRow === 'devices'}
          onToggle={() => toggle('devices')}
        >
          <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
            Device trust and account recovery are not recorded in this platform yet. Recovery runs through
            your organisation's own identity provider.
          </p>
        </AccountRow>

        <AccountRow
          kicker="No standing privilege"
          label="Privileged access"
          sub="Role alone grants no platform administration"
          open={openRow === 'privilege'}
          onToggle={() => toggle('privilege')}
        >
          <p className="m-0 text-[13px] leading-[21px] text-ink-dim">
            Being a Solution Partner gives you the clients you are assigned to and nothing else. Platform
            administration is a separate role, and it is not attached to this account.
          </p>
        </AccountRow>
      </div>

      <SaveBar
        dirty={dirty}
        busy={busy}
        label="Save account"
        onSave={save}
        onRevert={() => setName(account.display_name || '')}
      />
    </div>
  );
};

/* ── Security & data ────────────────────────────────────────────────────── */

const StatementRow: React.FC<{ statement: PartnerStatement }> = ({ statement }) => (
  <div className="flex items-start gap-4 border-t border-line-soft px-5 py-4 first:border-t-0">
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-canvas-inset font-sans text-[12px] font-bold text-ink-muted">
      S
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[12px] text-ink-dim">{statement.kicker}</div>
      <div className="text-[13.5px] font-bold text-ink">{statement.label}</div>
      <div className="text-[12.5px] text-ink-dim">
        {statement.value}
        {!statement.value && (
          <span className="text-ink-soft">{statement.unset || 'Not recorded'}</span>
        )}
      </div>
    </div>
    <span className="shrink-0 text-[12px] font-semibold text-ink-soft">
      {statement.enforced && 'Enforced'}
      {!statement.enforced && 'Not set'}
    </span>
  </div>
);

export const SecuritySection: React.FC<{ data: PartnerSettingsData }> = ({ data }) => {
  const { security } = data;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-panel bg-canvas-inset px-5 py-5 md:px-6">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
            Current boundary
          </div>
          <div className="mt-1.5 text-[24px] font-bold leading-tight tracking-[-0.01em] text-ink">
            {security.boundary_name}
          </div>
        </div>
        <span className="shrink-0 text-[13px] font-semibold text-ink">Policy active</span>
      </div>

      <div className="overflow-hidden rounded-panel border border-line bg-white">
        {security.statements.map((statement) => (
          <StatementRow key={statement.label} statement={statement} />
        ))}
      </div>

      {/* These four are organisation policy, not the partner's to set — and
          three of them are not stored anywhere yet, so they read as unset
          rather than showing a number nothing enforces. */}
      <div className="grid gap-5 sm:grid-cols-2">
        {security.policies.map((policy) => (
          <Field key={policy.label} label={policy.label}>
            <input
              value={policy.value || 'Not recorded in the platform yet'}
              readOnly
              className={READONLY}
            />
          </Field>
        ))}
      </div>

      <p className="m-0 text-[12.5px] text-ink-dim">
        Audit, holds, exports, and Context have separate policies. Policy managed by {security.managed_by}.
      </p>
    </div>
  );
};

/* ── Connectors ────────────────────────────────────────────────────────── */

const STATE_TONE: Record<string, string> = {
  Ready: 'text-ink',
  'Nothing yet': 'text-ink-soft',
  Unavailable: 'text-danger',
};

const AppRow: React.FC<{ entry: PartnerAppEntry }> = ({ entry }) => (
  <div className="flex flex-wrap items-start gap-4 border-t border-line-soft px-5 py-4 first:border-t-0">
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-canvas-inset font-sans text-[12px] font-bold text-ink-muted">
      {entry.mark}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[12px] text-ink-dim">{entry.state}</div>
      <div className="text-[13.5px] font-bold text-ink">{entry.label}</div>
      <div className="text-[12.5px] text-ink-dim">{entry.sub}</div>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[12px] text-ink-dim">Owner</div>
      <div className="text-[13.5px] font-bold text-ink">{entry.owner}</div>
      <div className="text-[12.5px] text-ink-dim">{entry.owner_sub}</div>
    </div>
    <span className={`shrink-0 text-[13px] font-semibold ${STATE_TONE[entry.state] || 'text-ink-muted'}`}>
      {entry.detail}
    </span>
  </div>
);

export const AppsSection: React.FC<{ data: PartnerSettingsData }> = ({ data }) => {
  const { apps } = data;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-panel bg-canvas-inset px-5 py-5 md:px-6">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">App catalog</div>
          <div className="mt-1.5 text-[20px] font-bold leading-tight text-ink">Apps available to your work</div>
          <p className="m-0 mt-1 text-[12.5px] leading-[20px] text-ink-dim">
            Catalog approval, connection, and source access remain separate. Being able to see a store here
            is not the same as holding rights to read what is in it.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-panel border border-line bg-white">
        {apps.entries.map((entry) => <AppRow key={entry.label} entry={entry} />)}
      </div>

      <p className="m-0 text-[12.5px] text-ink-dim">{apps.managed_by}</p>
    </div>
  );
};
